import { useState } from "react";
import { X } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import BookingModalContent from "./BookingModalContent";

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  podId: string;
  podName: string;
}

const BookingModal = ({ isOpen, onClose, podId, podName }: BookingModalProps) => {
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [provisioningToken, setProvisioningToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createSetupIntent = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch("https://qwipsvc.up.railway.app/api/setup-intent", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ pod_id: podId }),
      });
      
      if (!response.ok) {
        throw new Error("Failed to create setup intent");
      }

      const data = await response.json();
      setClientSecret(data.client_secret);
      setProvisioningToken(data.provisioning_jwt_token);
    } catch (err) {
      setError("Error – contact support for assistance.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStripeSuccess = () => {
    // After Stripe confirms the payment method, redirect to provision page with JWT token
    if (provisioningToken) {
      onClose();
      navigate(`/provision?t=${encodeURIComponent(provisioningToken)}`);
    }
  };

  const handleCloseModal = () => {
    setClientSecret(null);
    setProvisioningToken(null);
    setError(null);
    onClose();
  };

  // Create setup intent when modal opens
  if (isOpen && !clientSecret && !isLoading && !error) {
    createSetupIntent();
  }

  // Reset state when modal closes
  if (!isOpen && (clientSecret || error || provisioningToken)) {
    setClientSecret(null);
    setProvisioningToken(null);
    setError(null);
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div 
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ 
          duration: 0.4, 
          ease: [0.16, 1, 0.3, 1]
        }}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-[#374151]">
            Book {podName}
          </h2>
          <button
            onClick={handleCloseModal}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#374151]"></div>
              <span className="ml-3 text-gray-600">Initializing payment...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm font-medium">{error}</p>
              <button
                onClick={handleCloseModal}
                className="mt-4 w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          )}

          {clientSecret && provisioningToken && (
            <BookingModalContent
              clientSecret={clientSecret}
              onSuccess={handleStripeSuccess}
            />
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default BookingModal;
